/**
 * Plan Gating — journey-web shim.
 *
 * After the cornerstone refactor 2026-05 the gating logic lives in
 * `@tonnie/foundation/plan-gating`. This file is a thin adapter that
 * threads the journey-web `IJourneyInstance` + `IUser` shapes into the
 * canonical pure function — that's the only translation layer needed.
 */

import {
  checkAccess as checkAccessCore,
  canAccessFeature as canAccessFeatureCore,
  getFeatureAccessLevel as getFeatureAccessLevelCore,
  getFeatureLimits as getFeatureLimitsCore,
  shouldShowUpgradeCTA as shouldShowUpgradeCTACore,
  type AccessControlResult,
  type FeatureLimits,
  type FeatureType,
  type FundingSource,
  type JourneyForGating,
  type LifecycleState,
  type UserForGating,
} from '@tonnie/foundation';
import type { TFunction } from 'i18next';
import type { IJourneyInstance, IUser } from '@/core/types/auth.types';

export type { AccessControlResult, FeatureType };

function toJourney(instance?: IJourneyInstance | null): JourneyForGating | undefined {
  if (!instance) return undefined;
  // The canonical funding source + lifecycle live directly on the
  // instance now; no derivation needed.
  const heroes: string[] = [];
  if (instance.scope.checkup) heroes.push('checkup');
  if (instance.scope.teleportal) heroes.push('teleportal');
  if (instance.scope.qultureup) heroes.push('qultureup');
  if (instance.scope.care) heroes.push('care');
  if (instance.scope.passport) heroes.push('passport');
  return {
    fundingSource: instance.fundingSource as FundingSource,
    lifecycleState: instance.lifecycleState as LifecycleState,
    heroesEnabled: heroes,
  };
}

function toUser(user: IUser | null): UserForGating | null {
  if (!user) return null;
  // Derive `proSubscriptionActive` from the user's billing plan. The
  // legacy field was `user.plan` — kept here for back-compat with the
  // billing-side write that still populates that string.
  const plan = (user as unknown as { plan?: string }).plan;
  return {
    role: user.role as UserForGating['role'],
    proSubscriptionActive: plan === 'pro',
  };
}

export function getFeatureAccess(
  user: IUser | null,
  feature: FeatureType,
  journeyInstance?: IJourneyInstance | null
): AccessControlResult {
  return checkAccessCore(toUser(user), feature, toJourney(journeyInstance));
}

export function canAccessFeature(
  user: IUser | null,
  feature: FeatureType,
  journeyInstance?: IJourneyInstance | null
): boolean {
  return canAccessFeatureCore(toUser(user), feature, toJourney(journeyInstance));
}

export function getFeatureAccessLevel(
  user: IUser | null,
  feature: FeatureType,
  journeyInstance?: IJourneyInstance | null
): 'full' | 'limited' | 'none' {
  return getFeatureAccessLevelCore(toUser(user), feature, toJourney(journeyInstance));
}

export function shouldShowUpgradeCTA(user: IUser | null, feature: FeatureType): boolean {
  return shouldShowUpgradeCTACore(toUser(user), feature);
}

export function getFeatureLimits<K extends keyof FeatureLimits>(
  user: IUser | null,
  feature: K,
  journeyInstance?: IJourneyInstance | null
): FeatureLimits[K] {
  return getFeatureLimitsCore(toUser(user), feature, toJourney(journeyInstance));
}

/**
 * Where a locked hero leads: Billing only when paying is what unlocks it.
 *
 * Every locked hero in the sidebar used to open /billing. With no journey yet,
 * that sent a citizen who had just signed up to a payment page instead of the
 * Home that prepares the journey, and some left from there (1 Oct 2026).
 */
export type LockedHeroDestination = 'home' | 'billing';

export function lockedHeroDestination(access: AccessControlResult): LockedHeroDestination {
  switch (access.reason) {
    // A plan lock on a built journey: the upgrade is the answer.
    case 'tier_required':
    case 'hero_not_enabled':
      return 'billing';
    // Nothing to pay for yet: Home builds the journey, continues a draft,
    // resumes a pause and renews an expiry. And payment does not change a role.
    case 'no_journey':
    case 'journey_not_active':
    case 'role':
    case undefined:
      return 'home';
    default: {
      const exhaustive: never = access.reason;
      return exhaustive;
    }
  }
}

/** User-facing upgrade message per feature. */
export function getUpgradeMessage(t: TFunction, feature: FeatureType): string {
  const keys: Partial<Record<FeatureType, string>> = {
    // No `checkup` entry, and there must not be one. CheckUp is free on every
    // tier (`FREE_HERO_FEATURES` in @tonnie/foundation), so it can never reach
    // an upgrade prompt through gating; the key that used to sit here offered
    // "advanced CheckUp features" that do not exist. `plan.upgrade.checkup` is
    // deleted from en.json and every locale — a stranded key would just wait to
    // be re-used by mistake.
    teleportal: 'plan.upgrade.teleportal',
    qultureup: 'plan.upgrade.qultureup',
    care: 'plan.upgrade.care',
    passport: 'plan.upgrade.passport',
    career: 'plan.upgrade.career',
    copilot: 'plan.upgrade.copilot',
    journey_builder: 'plan.upgrade.journeyBuilder',
    family_program: 'plan.upgrade.familyProgram',
  };
  return t(keys[feature] ?? 'plan.upgrade.default');
}

/** Feature usage limit — only relevant for copilot today. */
export function getFeatureLimit(user: IUser | null, feature: FeatureType): number | 'unlimited' {
  if (!user) return 10;
  if (feature === 'copilot') {
    const plan = (user as unknown as { plan?: string }).plan;
    return plan === 'pro' ? 'unlimited' : 10;
  }
  return 10;
}

// Re-export shared limit configs for direct consumption by features
// that want to inspect "what does limited mean" — used by the
// LIMITED_FEATURE_CONFIG / FULL_FEATURE_CONFIG callers.
export { LIMITED_FEATURE_CONFIG, FULL_FEATURE_CONFIG } from '@tonnie/foundation';
export type { FeatureLimits } from '@tonnie/foundation';
